<script setup lang="ts">
import Button from '../../mixit-ui/components/button/Button.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { FreeMode, Pagination } from 'swiper/modules';
import { ref, onMounted, onUnmounted } from 'vue';
import type { Swiper as SwiperType } from 'swiper'
import DropdownCategories from '../dropdowns/DropdownCategories.vue';
import { categoryService, type EventCategory } from '@/services-new/categoryService';
import ImageRemote from '@/mixit-ui/components/media/ImageRemote.vue';

const swiperInstance = ref<SwiperType | null>(null)
const isBeginning = ref(true)
const isEnd = ref(false)

const subMenuRef = ref<HTMLElement | null>(null)
const categoriesMaxHeight = ref('85dvh')
const eventCategories = ref<EventCategory[]>([])

function updateCategoriesHeight() {
  if (subMenuRef.value) {
    const bottom = subMenuRef.value.getBoundingClientRect().bottom
    categoriesMaxHeight.value = `calc(100dvh - ${bottom}px)`
  }
}

onMounted(async () => {
  window.addEventListener('resize', updateCategoriesHeight)
  window.addEventListener('scroll', updateCategoriesHeight)
  updateCategoriesHeight()
  eventCategories.value = await categoryService.loadEventCategories()
})
onUnmounted(() => {
  window.removeEventListener('resize', updateCategoriesHeight)
  window.removeEventListener('scroll', updateCategoriesHeight)
})

function onSwiper(instance: SwiperType) {
  swiperInstance.value = instance
}

function onProgress() {
  if (!swiperInstance.value) return
  isBeginning.value = swiperInstance.value.isBeginning
  isEnd.value = swiperInstance.value.isEnd
}
</script>

<template>
  <header
    ref="subMenuRef"
    class="bg-white px-10 hidden lg:flex flex-row h-18 items-center w-full gap-12"
  >
    <div class="flex flex-row gap-2">
      <VDropdown
        distance="14"
        :arrow-padding="0"
        :triggers="['click', 'hover', 'focus']"
        :popper-triggers="['click', 'hover', 'focus']"
        popper-class="no-arrow no-style-popper p-0 w-screen"
      >
        <Button
          icon="mi-bird"
          label="Všechny produkty"
          size="md"
        />
        <template #popper>
          <DropdownCategories :max-height="categoriesMaxHeight" />
        </template>
      </VDropdown>
      <Button
        icon="mi-mix"
        label="Mixovat vlastní"
        severity="secondary"
        size="md"
      />
    </div>
    <div class="overflow-hidden flex-1 relative flex h-full items-center">
      <Swiper
        :free-mode="true"
        :space-between="24"
        slides-per-view="auto"
        :mousewheel="{
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: true,
        }"
        :modules="[FreeMode, Pagination]"
        class="w-full overflow-visible!"
        @swiper="onSwiper"
        @progress="onProgress"
      >
        <SwiperSlide
          v-for="category in eventCategories"
          :key="category.systemName"
          class="w-auto!"
        >
          <RouterLink
            :to="{ name: 'productsPath', params: { permalink: category.permalink } }"
            class="font-roboto-slab font-bold text-brown-600 text-sm hover:underline flex flex-row gap-1 items-center"
          >
            <ImageRemote
              v-if="category.image"
              :data="{ uri: `https://img.mix.it/general_assets/categories/${category.image}` }"
              img-class="w-4"
            />
            <span>{{ category.title }}</span>
          </RouterLink>
        </SwiperSlide>
      </Swiper>
      <Button
        v-if="!isBeginning"
        icon="mi-chevron-l"
        severity="secondary"
        size="md"
        class="absolute left-0 top-1/2 -translate-y-1/2 z-3"
        @click="swiperInstance?.slidePrev()"
      />
      <Button
        v-if="!isEnd"
        icon="mi-chevron-r"
        severity="secondary"
        size="md"
        class="absolute right-0 top-1/2 -translate-y-1/2 z-3"
        @click="swiperInstance?.slideNext()"
      />
    </div>
  </header>
</template>
